import { useCallback, useEffect, useState } from 'react';

const DEFAULT_PULL_THRESHOLD = 80;

interface UsePullToRefreshOptions {
  isRefreshing: boolean;
  onRefresh: () => Promise<void> | void;
  threshold?: number;
}

interface UsePullToRefreshResult {
  pullDelta: number;
  threshold: number;
}

const isPullToRefreshDisabled = () =>
  document.body.classList.contains('no-pull-to-refresh');

const isPageScrolledToTop = () => window.scrollY === 0;

export const usePullToRefresh = ({
  isRefreshing,
  onRefresh,
  threshold = DEFAULT_PULL_THRESHOLD,
}: UsePullToRefreshOptions): UsePullToRefreshResult => {
  const [startY, setStartY] = useState(0);
  const [pullDelta, setPullDelta] = useState(0);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (isPullToRefreshDisabled()) return;
    if (isPageScrolledToTop()) {
      setStartY(event.touches[0].screenY);
    }
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (isPullToRefreshDisabled()) return;
    if (startY === 0) return;

    const currentY = event.touches[0].screenY;
    const delta = currentY - startY;

    if (delta > 0 && isPageScrolledToTop()) {
      setPullDelta(Math.min(delta / 2, threshold));
      event.preventDefault();
      return;
    }

    setStartY(0);
    setPullDelta(0);
  }, [startY, threshold]);

  const handleTouchEnd = useCallback(() => {
    setStartY(0);
    if (pullDelta >= threshold && !isRefreshing) {
      void onRefresh();
    }
    setPullDelta(0);
  }, [isRefreshing, onRefresh, pullDelta, threshold]);

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchEnd, handleTouchMove, handleTouchStart]);

  return {
    pullDelta,
    threshold,
  };
};

export default usePullToRefresh;
